import Service, { service } from '@ember/service';
import type TasApplicationModel from '../models/tas-application';
import type TasProgramInstanceModel from '../models/tas-program-instance';
import type { Step } from '../components/tio/progress-tracker-items.gts';
import type { IntlService } from 'ember-intl';
import type {
  ApplicationApprovalSummary,
  TransitionLogSignature,
} from '../types/tuition-assistance';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from './session-context.ts';
import type EmployeeModel from '../models/employee.ts';
import { tracked } from '@glimmer/tracking';
import type Owner from '@ember/owner';
import { PhArticle, PhCalendar, PhCheck, PhPerson, PhWarning } from 'ember-phosphor-icons';

/**
 * A service for building progress (bubbles) of applications.
 *
 * @memberof services
 */
const formatTimestampToMMDDYYYY = (timestamp: string) => {
  const date = new Date(timestamp);

  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

const logDate = (model: TasApplicationModel | TasProgramInstanceModel, eventString: string) => {
  const timeStamp = model?.transitionLog.find((log) => log.event === eventString)?.timestamp;
  return formatTimestampToMMDDYYYY(timeStamp || '');
};

const getBy = (application: TasApplicationModel, event: string) => {
  const log = application.transitionLog.find((log) => log.event === event);
  return log?.reason?.by?.name;
};

export default class ProgressBuilderService extends Service {
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;

  @tracked employees: EmployeeModel[];

  constructor(owner: Owner) {
    super(owner);
    // TODO: Why don't we just use the getter. Why the tracked object?
    this.employees = this.adminEmployees;
  }

  get adminEmployees() {
    return this.store.peekAll('employee') as EmployeeModel[];
  }

  stepLabel(key: string): string {
    const translationKey = `common.progress_tracker.${key}`;
    return this.intl.t(translationKey);
  }

  isStepCompleted(application: TasApplicationModel, log: string): boolean {
    return application.transitionLog.some((logEntry) => logEntry.from === log);
  }

  isProgramStepCompleted(program: TasProgramInstanceModel, log: string): boolean {
    return program.transitionLog.some((logEntry) => logEntry.from === log);
  }

  isProgramStepInProgress(program: TasProgramInstanceModel, log: string): boolean {
    return program.transitionLog.some((logEntry) => logEntry.to === log);
  }

  isInProgress(state: string, possibleStates: string[]): boolean {
    return possibleStates.includes(state);
  }

  actionNeeded(state: string, actionNeededStates: string[]): boolean {
    return actionNeededStates.includes(state);
  }

  startApplicationStep(application: TasApplicationModel): Step {
    return {
      title: this.stepLabel('start_application'),
      completed: application ? true : false,
      details: [
        {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhCalendar,
            detail: `Application Started ${formatTimestampToMMDDYYYY(application.createdAt)} `,
          },
        },
        {
          componentName: 'FullHistory',
          props: {
            application: application,
          },
        },
      ],
    } as unknown as Step;
  }

  submitForApprovalStep(application: TasApplicationModel): Step {
    const template = application.tasProgramInstance.tasProgramTemplate;
    if (template.courseApprovalRequired) {
      return {
        title: this.stepLabel('submit_for_approval'),
        completed: this.isStepCompleted(application, 'TAS.ApplicationState.DEFINE_COURSES'),
        inProgress: this.isInProgress(application.state, ['TAS.ApplicationState.DEFINE_COURSES']),
        details: [
          {
            componentName: 'DetailWithIcon',
            props: {
              icon: PhPerson,
              detail: `Submitted by ${getBy(application, 'request_courses_approval')}`,
            },
          },
          {
            componentName: 'FullHistory',
            props: {
              application: application,
            },
          },
        ],
      } as unknown as Step;
    }
    return {} as Step;
  }

  applicationApprovalStep(application: TasApplicationModel): Step {
    const template = application.tasProgramInstance.tasProgramTemplate;
    if (template.courseApprovalRequired) {
      return {
        title: this.stepLabel('application_approval'),
        completed: this.isStepCompleted(
          application,
          'TAS.ApplicationState.PENDING_COURSES_APPROVAL'
        ),
        inProgress: this.isInProgress(application.state, [
          'TAS.ApplicationState.DEFINE_COURSES',
          'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
        ]),
        actionNeeded: this.actionNeeded(application.state, [
          'TAS.ApplicationState.COURSES_NOT_APPROVED',
          'TAS.ApplicationState.COURSES_REJECTED',
        ]),
        details: [
          {
            componentName: 'ApproverDetail',
            props: {
              icon: PhPerson,
              application: application,
              stepToApprove: 'course_approval',
            },
          },
          {
            componentName: 'FullHistory',
            props: {
              application: application,
            },
          },
        ],
      } as unknown as Step;
    }
    return {} as Step;
  }

  inProgressStep(application: TasApplicationModel): Step {
    return {
      title: this.stepLabel('in_progress'),
      completed: this.isStepCompleted(application, 'TAS.ApplicationState.ATTEND'),
      inProgress: this.isInProgress(application.state, ['TAS.ApplicationState.ATTEND']),
    } as unknown as Step;
  }

  submitEvidenceStep(application: TasApplicationModel): Step {
    const submissionDetails = () => {
      const submitter = getBy(application, 'request_evidence_approval');
      if (submitter) {
        return `Submitted by ${submitter}`;
      } else {
        return 'Evidence not submitted';
      }
    };

    return {
      title: this.stepLabel('submit_evidence'),
      completed: this.isStepCompleted(application, 'TAS.ApplicationState.ATTEND'),
      details: [
        {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhPerson,
            detail: submissionDetails(),
          },
        },
        {
          componentName: 'FullHistory',
          props: {
            application: application,
          },
        },
      ],
    } as unknown as Step;
  }

  evidenceApprovalStep(application: TasApplicationModel): Step {
    const hasEvidence = () => {
      if (application.applicationHasSubmittedForEvidenceApproval) {
        return {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhPerson,
            detail: `Submitted by ${getBy(application, 'request_evidence_approval')}`,
          },
        };
      } else {
        return {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhArticle,
            detail: 'Grades or receipts not submitted',
          },
        };
      }
    };

    const evidenceApproved = () => {
      if (application.state === 'TAS.ApplicationState.EVIDENCE_REJECTED') {
        return {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhWarning,
            iconBgColor: 'bg-red-700',
            detail: `Evidence rejected on ${logDate(application, 'reject_evidence')} by ${getBy(application, 'reject_evidence')}`,
          },
        };
      } else {
        return {
          componentName: 'ApproverDetail',
          props: {
            icon: PhPerson,
            application: application,
            stepToApprove: 'evidence_approval',
          },
        };
      }
    };

    if (application.tasProgramInstance.tasProgramTemplate.evidenceApprovalRequired) {
      return {
        title: this.stepLabel('evidence_approval'),
        completed: this.isStepCompleted(application, 'TAS.ApplicationState.EVIDENCE_APPROVED'),
        inProgress: this.isInProgress(application.state, [
          'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        ]),
        actionNeeded: this.actionNeeded(application.state, [
          'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
          'TAS.ApplicationState.EVIDENCE_REJECTED',
        ]),
        details: [
          hasEvidence(),
          evidenceApproved(),
          {
            componentName: 'FullHistory',
            props: {
              application: application,
            },
          },
        ],
      } as unknown as Step;
    }
    return {} as Step;
  }

  completedStep(application: TasApplicationModel): Step {
    const complete = () => {
      if (application.state === 'TAS.ApplicationState.FULFILLED') {
        return {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhCheck,
            iconBgColor: 'bg-green-600',
            detail: `Completed on ${logDate(application, 'fulfill')}`,
          },
        };
      } else {
        return {
          componentName: 'DetailWithIcon',
          props: {
            icon: PhCalendar,
            detail: `Not yet completed.`,
          },
        };
      }
    };

    return {
      title: this.stepLabel('completed'),
      completed: application.state === 'TAS.ApplicationState.FULFILLED',
      actionNeeded: application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED',
      inProgress: this.isInProgress(application.state, ['TAS.ApplicationState.EVIDENCE_APPROVED']),
      details: [
        complete(),
        {
          componentName: 'FullHistory',
          props: {
            application: application,
          },
        },
      ],
    } as unknown as Step;
  }

  applicationStepList = (application: TasApplicationModel): Step[] => {
    return [
      this.startApplicationStep(application),
      this.submitForApprovalStep(application),
      this.applicationApprovalStep(application),
      this.inProgressStep(application),
      this.submitEvidenceStep(application),
      this.evidenceApprovalStep(application),
      this.completedStep(application),
    ];
  };

  applicationProcess = (application?: TasApplicationModel): Step[] => {
    if (!application) {
      return [
        { title: this.intl.t(`tuition_assistance.program_steps.start_application`) },
        { title: this.intl.t(`tuition_assistance.program_steps.submit_for_evidence_approval`) },
        {
          title: this.intl.t(`tuition_assistance.program_steps.evidence_approval`),
        },
        { title: this.intl.t(`tuition_assistance.program_steps.complete`) },
      ];
    } else {
      return [
        this.startApplicationStep(application),
        this.submitEvidenceStep(application),
        this.evidenceApprovalStep(application),
        this.completedStep(application),
      ];
    }
  };

  programProgress = (program: TasProgramInstanceModel): Step[] => {
    return [
      {
        title: this.stepLabel('start_program'),
        completed: true,
      },
      {
        title: this.stepLabel('submit_for_approval'),
        completed: this.isProgramStepCompleted(program, 'TAS.ProgramInstanceState.DRAFT'),
        inProgress: this.isProgramStepInProgress(program, 'TAS.ProgramInstanceState.CREATED'),
      },
      {
        title: this.stepLabel('program_approval'),
        completed: this.isProgramStepCompleted(
          program,
          'TAS.ProgramInstanceState.PROGRAM_APPROVED'
        ),
        inProgress: this.isProgramStepInProgress(
          program,
          'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'
        ),
      },
      {
        title: this.stepLabel('enrolled'),
        completed: program.isCompleted
          ? true
          : this.isProgramStepCompleted(
              program,
              'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL'
            ),
        inProgress: this.isProgramStepInProgress(program, 'TAS.ProgramInstanceState.PRIMED'),
      },
      {
        title: this.stepLabel('mark_program_complete'),
        completed: program.isCompleted
          ? true
          : this.isProgramStepCompleted(program, 'TAS.ProgramInstanceState.COMPLETION_APPROVED'),
        inProgress: this.isProgramStepInProgress(
          program,
          'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL'
        ),
      },
      {
        title: this.stepLabel('completed'),
        completed: program.isCompleted
          ? true
          : this.isProgramStepCompleted(
              program,
              'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL'
            ),
        inProgress: false,
      },
    ];
  };

  fetchApprovers = async () => {
    const currentEmployee = this.sessionContext.currentEmployee;
    if (!currentEmployee) {
      console.warn('[progress-builder] fetchApprovers called without a currentEmployee');
      return;
    }
    this.employees = await this.store.query('employee', {
      filter: {
        company_id: currentEmployee.company.id,
      },
    });
  };

  findApproverNameFromTransitionLog(log: TransitionLogSignature[], targetEvent: string) {
    for (const event of log) {
      if (event.event === targetEvent && event.reason && event.reason.by) {
        return event.reason.by.name;
      }
    }
  }

  getApproversForStep = (application: TasApplicationModel, step: string) => {
    const approverName = this.findApproverNameFromTransitionLog(
      application.transitionLog,
      'approve_courses'
    );

    const approvalQueue = (step: string): ApplicationApprovalSummary => {
      switch (step) {
        case 'course_approval':
          return application.coursesApproval;
        case 'evidence_approval':
          return application.evidenceApproval;
        default:
          return {
            affirmed: [],
            current_reviewers: [],
            possible_reviewers: [],
            required: [],
            approved: false,
          };
      }
    };

    const affirmedApprovers = () => {
      const reviewers = [];
      for (const reviewer of approvalQueue(step).affirmed) {
        if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
          reviewers.push({ firstName: 'TIO', lastName: 'Approver' });
        } else {
          // @ts-expect-error: figure out id types
          const employee = this.employees.find((employee) => employee.id == reviewer.employee_id);
          if (employee) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            employee && reviewers.push(employee);
          } else {
            reviewers.push({ firstName: approverName });
          }
        }
      }
      return reviewers.filter(
        (obj1, i, arr) =>
          arr.findIndex((obj2) =>
            // @ts-expect-error: key is a string
            ['firstName', 'lastName'].every((key) => obj2[key] === obj1[key])
          ) === i
      );
    };

    const currentApprovers = () => {
      const reviewers = [];
      for (const reviewer of approvalQueue(step).current_reviewers) {
        if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
          reviewers.push({ firstName: 'TIO', lastName: 'Approver' });
        } else {
          // @ts-expect-error: figure out id types
          const employee = this.employees.find((employee) => employee.id == reviewer.employee_id);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          employee && reviewers.push(employee);
        }
      }
      return reviewers.filter(
        (obj1, i, arr) =>
          arr.findIndex((obj2) =>
            // @ts-expect-error: key is a string
            ['firstName', 'lastName'].every((key) => obj2[key] === obj1[key])
          ) === i
      );
    };

    const possibleApprovers = () => {
      const reviewers = [];
      for (const reviewer of approvalQueue(step).possible_reviewers) {
        if (reviewer.dot === 'TAS.Approver.TIO_APPROVER') {
          reviewers.push({ firstName: 'TIO', lastName: 'Approver' });
        } else {
          // @ts-expect-error: figure out id types
          const employee = this.employees.find((employee) => employee.id == reviewer.employee_id);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          employee && reviewers.push(employee);
        }
      }
      return reviewers.filter(
        (obj1, i, arr) =>
          arr.findIndex((obj2) =>
            // @ts-expect-error: key is a string
            ['firstName', 'lastName'].every((key) => obj2[key] === obj1[key])
          ) === i
      );
    };

    return {
      possibleApprovers: possibleApprovers(),
      affirmedApprovers: affirmedApprovers(),
      currentApprovers: currentApprovers(),
    };
  };
}
